import {
    U2bColumnDefinitionExternalComponent,
    U2bColumnPipeName,
    U2bTableData
} from '@core/components/layout/table/table.types';
import { BcmUserTableSettingKey } from '@shared/models/bcm-settings-user';
import { getIsActive } from '@core/functions/get-is-active';
import { Berth, BerthStatus, berthStatusTranslationKeys } from '@shared/models/berth';
import { FilterFieldType } from '@core/datafilter/available-filter-fields';

export const berthsTableData: U2bTableData = {
    tableId: BcmUserTableSettingKey.Berths,
    hasServerSideFiltering: true,
    activeSortColumn: 'pierHandle',
    columnDefinitions: [
        {
            property: 'statusIndex',
            name: 'Status',
            translationKey: 'bcmBerths.table.status',
            hideNameInHeader: true,
            exportKey: null,
            isActive: getIsActive(),
            disableSort: true,
            externalComponent: U2bColumnDefinitionExternalComponent.BerthsStatusIndicator,
            filter: {
                property: 'status',
                fieldType: FilterFieldType.Select,
                selectOptions: Object.values(BerthStatus),
                selectOptionsTranslations: berthStatusTranslationKeys
            },
        },
        {
            property: 'pierHandle',
            name: 'Steg',
            translationKey: 'bcmBerths.table.pier',
            isActive: getIsActive(),
            exportKey: 'pierHandle',
            parseItem: (item: Berth) => item.pier?.id ? item.pier.handle : '',
            filter: {
                property: 'pier',
            },
        },
        {
            property: 'handle',
            name: 'Liegeplatz',
            translationKey: 'bcmBerths.table.berth',
            isActive: getIsActive(),
            filter: {},
        },
        {
            // `type` is an aggregated client-side label built from boolean flags by Berth.getType().
            // No clean server-side filter — use the per-flag filters below (isDoubleBox, isJetSki, etc.).
            property: 'type',
            name: 'Typ',
            translationKey: 'bcmBerths.table.type',
            isActive: getIsActive(false, false, false),
            disableSort: true,
        },
        {
            property: 'category',
            name: 'Kategorie',
            translationKey: 'bcmBerths.table.category',
            isActive: getIsActive(false, false, false),
            exportKey: 'category.name',
            parseItem: (item: Berth) => item.category?.name ?? '',
            filter: {},
        },
        {
            property: 'length',
            name: 'Länge',
            translationKey: 'bcmBerths.table.length',
            isActive: getIsActive(),
            pipe: U2bColumnPipeName.Decimal,
            filter: {
                fieldType: FilterFieldType.Numeric
            },
        },
        {
            property: 'width',
            name: 'Breite',
            translationKey: 'bcmBerths.table.width',
            isActive: getIsActive(),
            pipe: U2bColumnPipeName.Decimal,
            filter: {
                fieldType: FilterFieldType.Numeric
            },
        },
        {
            property: 'm²',
            name: 'm²',
            translationKey: 'bcmBerths.table.squareMeters',
            isActive: getIsActive(),
            exportKey: 'm²',
            pipe: U2bColumnPipeName.Decimal,
            parseItem: (item: Berth) => item.squareMeters,
            filter: {
                property: 'squareMeters',
                fieldType: FilterFieldType.Numeric
            },
        },
        {
            property: 'maxDraft',
            name: 'Tiefe',
            translationKey: 'bcmBerths.table.maxDraft',
            isActive: getIsActive(),
            pipe: U2bColumnPipeName.Decimal,
            filter: {
                fieldType: FilterFieldType.Numeric
            },
        },
        {
            property: 'bookingId',
            name: 'Buchungsnr.',
            translationKey: 'bcmBerths.table.bookingNo',
            isActive: getIsActive(false, false, false),
            exportKey: null,
            filter: {},
            parseItem: (item: Berth) => {
                const assignment = item.assignments?.length ?
                    item.assignments[0] : null;

                return assignment?.booking;
            },
            externalComponent: U2bColumnDefinitionExternalComponent.BcmBookingLink,
        },
        {
            property: 'boatName',
            name: 'Bootsname',
            isActive: getIsActive(),
            exportKey: 'boatName',
            translationKey: 'boatName',
            externalComponent: U2bColumnDefinitionExternalComponent.BerthsAssignments,
            externalComponentMetaData: {
                showName: true
            },
            filter: {},
        },
        {
            property: 'boatLicensePlate',
            name: 'Kennzeichen',
            translationKey: 'bcmBerths.table.licensePlate',
            isActive: getIsActive(),
            exportKey: 'boatLicensePlate',
            externalComponent: U2bColumnDefinitionExternalComponent.BerthsAssignments,
            externalComponentMetaData: {
                showLicensePlate: true
            },
            filter: {},
        },
        {
            property: 'boatManufacturer',
            name: 'Boots&shy;hersteller',
            isActive: getIsActive(),
            exportKey: 'boatManufacturer',
            translationKey: 'boats.table.boatManufacturer',
            externalComponent: U2bColumnDefinitionExternalComponent.BerthsAssignments,
            externalComponentMetaData: {
                showManufacturer: true
            },
            filter: {},
        },
        {
            property: 'boatOwner',
            name: 'Eigner&nbsp;/ Kontakt',
            isActive: getIsActive(),
            exportKey: 'boatOwner',
            translationKey: 'accounting.financialDocuments.ownerContactColumn',
            externalComponent: U2bColumnDefinitionExternalComponent.BerthsAssignments,
            externalComponentMetaData: {
                showOwner: true
            },
            filter: {},
        },
        // new display columns from ticket 3386
        {
            property: 'isDoubleBox',
            name: 'Doppelbox',
            translationKey: 'bcmBerths.table.doubleBox',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Berth) => item.isDoubleBox ? '✔' : '',
            filter: {
                fieldType: FilterFieldType.Boolean
            },
        },
        {
            property: 'isBuoy',
            name: 'Boje',
            translationKey: 'bcmBerths.table.buoy',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Berth) => item.isBuoy ? '✔' : '',
            filter: {
                fieldType: FilterFieldType.Boolean
            },
        },
        {
            // just needed in case it's a buoy
            property: 'maxCarryingCapacity',
            name: 'Max. Tragkraft',
            translationKey: 'bcmBerths.table.maxCarryingCapacity',
            isActive: getIsActive(false, false, false),
            pipe: U2bColumnPipeName.Number,
            filter: {
                fieldType: FilterFieldType.Numeric
            },
        },
        {
            property: 'isJetSki',
            name: 'Jetski',
            translationKey: 'bcmBerths.table.jetSki',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Berth) => item.isJetSki ? '✔' : '',
            filter: {
                fieldType: FilterFieldType.Boolean
            },
        },
        {
            property: 'isLiftStation',
            name: 'Hebeanlage',
            translationKey: 'bcmBerths.table.heBeanlage',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Berth) => item.isLiftStation ? '✔' : '',
            filter: {
                fieldType: FilterFieldType.Boolean
            },
        },
        {
            property: 'forGuest',
            name: 'für Gäste',
            translationKey: 'bcmBerths.table.forGuest',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Berth) => item.forGuest ? '✔' : '',
            filter: {
                fieldType: FilterFieldType.Boolean
            },
        },
        {
            property: 'forLongTermResidents',
            name: 'für Dauergastlieger',
            translationKey: 'bcmBerths.table.forLongTermResidents',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Berth) => item.forLongTermResidents ? '✔' : '',
            filter: {
                fieldType: FilterFieldType.Boolean
            },
        },
        {
            property: 'isExceptionBerth',
            name: 'Sonderliegeplatz',
            translationKey: 'bcmBerths.table.exceptionBerth',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Berth) => item.isExceptionBerth ? '✔' : '',
            filter: {
                fieldType: FilterFieldType.Boolean
            },
        },
        {
            property: 'isSeasonalBerth',
            name: 'Saisonliegeplatz',
            translationKey: 'bcmBerths.table.seasonalBerth',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Berth) => item.isSeasonalBerth ? '✔' : '',
            filter: {
                fieldType: FilterFieldType.Boolean
            },
        },
        {
            property: 'isCommercialBerth',
            name: 'Gewerbeliegeplatz',
            translationKey: 'bcmBerths.table.commercialBerth',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Berth) => item.isCommercialBerth ? '✔' : '',
            filter: {
                fieldType: FilterFieldType.Boolean
            },
        },
        {
            property: 'isVBerth',
            name: 'VLiegeplatz',
            translationKey: 'bcmBerths.table.vBerth',
            isActive: getIsActive(false, false, false),
            parseItem: (item: Berth) => item.isVBerth ? '✔' : '',
            filter: {
                fieldType: FilterFieldType.Boolean
            },
        },
        {
            property: 'statusText',
            name: 'Status Text',
            translationKey: 'bcmBerths.table.statusText',
            isActive: getIsActive(false, false, false),
            exportKey: null,
            externalComponent: U2bColumnDefinitionExternalComponent.BerthsReservationInfo,
            filter: {}
        },
        {
            property: 'note',
            name: 'Notiz',
            translationKey: 'bcmBerths.table.note',
            isActive: getIsActive(false, false, false),
            filter: {},
        },
    ],
};
